import { Injectable } from '@angular/core';
import { IClient } from 'ngx-mat-esign360/lib/transaction/model/client.model';
import {
  IDocumentCheckbox,
  IDocumentSignField,
  IDocumentCodeSignField,
  IDocumentHandSignField,
} from 'ngx-mat-esign360/lib/transaction/model/document-field.model';
import { IDocumentSigner } from 'ngx-mat-esign360/lib/transaction/model/document-signer.model';
import { TxDocument } from 'ngx-mat-esign360/lib/transaction/model/document.model';
import {
  IDtoClient,
  IDtoClientStateData,
  IDtoDocument,
  IDtoDocumentCheckbox,
  IDtoDocumentSignField,
  IDtoPrerequisite,
  IDtoTransaction,
  IDtoTransactionSection,
} from 'ngx-mat-esign360/lib/transaction/model/dtos/default/dto.model';
import { SignatureType } from 'ngx-mat-esign360/lib/transaction/model/signature-type.model';
import { Transaction } from 'ngx-mat-esign360/lib/transaction/model/transaction.model';
import { IClientStateData } from '../model/client-state-data.model';
import { DtoDocumentSignFieldState } from '../model/dtos/default/document-field-state.model';
import {
  DtoCommonDocumentState,
  DtoSignDocumentState,
} from '../model/dtos/default/document-state.model';
import { DtoTransactionState } from '../model/dtos/default/transaction-state.model';
import { IPrerequisite } from '../model/prerequisite.model';
import { TransactionSection } from '../model/transaction-section.model';
import { DtoParserService } from './dto-parser.service';

@Injectable()
export class DefaultDtoParserService extends DtoParserService {
  constructor() {
    super();
  }

  parseTransaction(dtoTransaction: IDtoTransaction): Transaction {
    return new Transaction(
      dtoTransaction.transactionId,
      dtoTransaction.externalId,
      dtoTransaction.correlationId,
      dtoTransaction.title,
      dtoTransaction.presentState,
      dtoTransaction.state !== DtoTransactionState.TransactionStart &&
        dtoTransaction.state !== DtoTransactionState.UpdateDtoFlowStarting &&
        dtoTransaction.state !== DtoTransactionState.Validating,
      new Date(dtoTransaction.startTime),
      dtoTransaction.redirectUrl,
      this.parseClients(
        dtoTransaction.clients,
        dtoTransaction.clientStates,
        dtoTransaction.prerequisites || []
      ),
      this.parseTransactionSections(dtoTransaction.sections)
    );
  }

  parseClients(
    dtoClients: IDtoClient[],
    dtoClientStatesData: IDtoClientStateData[] = [],
    dtoPrerequisites: IDtoPrerequisite[] = []
  ): IClient[] {
    return dtoClients.map((client) => {
      const { clientId, ...clientInfo } = client;
      return {
        ...clientInfo,
        id: clientId,
        stateData: this.parseClientStateData(
          dtoClientStatesData.find(
            (dtoClientStateData) => dtoClientStateData.clientId === client.clientId
          )
        ),
        prerequisites: this.parsePrerequisites(
          dtoPrerequisites.filter((dtoPrerequisite) => dtoPrerequisite.clientId === client.clientId)
        ),
      };
    });
  }

  parseClientStateData(dtoClientStateData: IDtoClientStateData): IClientStateData {
    const { presentState, clientId, ...otherClientStateData } = dtoClientStateData;
    return { ...otherClientStateData, signingState: presentState };
  }

  parsePrerequisites(dtoPrerequisites: IDtoPrerequisite[]): IPrerequisite[] {
    return dtoPrerequisites.map((dtoPrerequisite) => ({
      id: dtoPrerequisite.prerequisiteId,
      externalId: dtoPrerequisite.externalId,
      type: dtoPrerequisite.type,
      state: dtoPrerequisite.state,
      title: dtoPrerequisite.title,
      text: dtoPrerequisite.text,
      configuration: dtoPrerequisite.configuration,
      subtype: dtoPrerequisite.subtype,
      validateInfo: dtoPrerequisite.validateInfo,
    }));
  }

  parseTransactionSections(dtoTransactionSections: IDtoTransactionSection[]): TransactionSection[] {
    return dtoTransactionSections.map(
      (section) => new TransactionSection(section.sectionId, this.parseDocuments(section.documents))
    );
  }

  parseDocuments(dtoDocuments: IDtoDocument[]): TxDocument[] {
    return dtoDocuments.map(
      (dtoDocument) =>
        new TxDocument(
          dtoDocument.documentId,
          dtoDocument.documentName,
          dtoDocument.dfid,
          dtoDocument.actionType,
          dtoDocument.documentMetadata,
          dtoDocument.state === DtoCommonDocumentState.DocumentDisabled ||
            dtoDocument.state === DtoCommonDocumentState.PreparingDocument,
          dtoDocument.state === DtoCommonDocumentState.DocumentProcessed ||
            dtoDocument.state === DtoCommonDocumentState.ProcessedDocumentRead ||
            dtoDocument.state === DtoCommonDocumentState.Finalized,
          dtoDocument.state === DtoSignDocumentState.DocumentLocked,
          dtoDocument.state === DtoSignDocumentState.InsertingSignature,
          dtoDocument.state === DtoCommonDocumentState.Finalized,
          this.parseSigners(dtoDocument)
        )
    );
  }

  parseSigners(dtoDocument: IDtoDocument): IDocumentSigner[] {
    return dtoDocument.signers.map((dtoSigner) => ({
      clientId: dtoSigner.clientId,
      relation: dtoSigner.action,
      signingState: dtoSigner.presentState,
      signFields: this.parseSignFields(dtoSigner.signatures),
      checkboxes: this.parseCheckboxes(dtoSigner.formFields),
    }));
  }

  parseCheckboxes(dtoCheckboxes: IDtoDocumentCheckbox[]): IDocumentCheckbox[] {
    return (
      dtoCheckboxes?.map((checkbox) => ({
        id: checkbox.formFieldId,
        pdfId: checkbox.formFieldPdfId,
        checked: checkbox.formFieldValue === 'checked',
        required: checkbox.required,
        visible: checkbox.visible,
      })) || []
    );
  }

  parseSignFields(dtoSignFields: IDtoDocumentSignField[]): IDocumentSignField[] {
    return (
      dtoSignFields?.map((dtoSignField) => {
        if (dtoSignField.type === SignatureType.SMS || dtoSignField.type === SignatureType.Email) {
          return this.parseCodeSignField(dtoSignField);
        }
        return this.parseHandSignField(dtoSignField);
      }) || []
    );
  }

  parseSignField(dtoSignField: IDtoDocumentSignField): IDocumentSignField {
    return {
      id: dtoSignField.signatureId,
      pdfId: dtoSignField.signFieldPdfId,
      type: dtoSignField.type || SignatureType.Hand,
      disabled:
        dtoSignField.state === DtoDocumentSignFieldState.SignatureStart ||
        dtoSignField.state === DtoDocumentSignFieldState.SignatureDisabled ||
        dtoSignField.state === DtoDocumentSignFieldState.Signed,
      signed: dtoSignField.state === DtoDocumentSignFieldState.Signed,
    };
  }

  parseHandSignField(dtoSignField: IDtoDocumentSignField): IDocumentHandSignField {
    return {
      ...this.parseSignField(dtoSignField),
      allowedHandDeviceTypes: dtoSignField.allowedHandDeviceTypes || null,
    };
  }

  parseCodeSignField(dtoSignField: IDtoDocumentSignField): IDocumentCodeSignField {
    return {
      ...this.parseSignField(dtoSignField),
      attemptsLeft:
        (typeof dtoSignField.attemptsLeft === 'string'
          ? parseInt(dtoSignField.attemptsLeft, 10)
          : dtoSignField.attemptsLeft) ?? 1,
      expireTime: new Date(dtoSignField.expireTime),
    };
  }
}
